import type * as React from 'react';
import { cn } from '../lib/cn.js';

export interface PageHeaderProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}

/**
 * En-tête de page obligatoire : aucun écran ne rend son propre h1.
 */
export function PageHeader({ title, description, actions, className, ...props }: PageHeaderProps) {
  /**
   * `<p>` n'accepte que du contenu en ligne. Une description faite de badges — qui sont des
   * `<div>` — est un HTML invalide : le navigateur ferme le paragraphe de lui-même et recompose
   * l'arbre à sa façon, et React s'en plaint à chaque rendu. Du texte reste donc un paragraphe ;
   * tout le reste prend un `<div>` portant **exactement** les mêmes classes.
   *
   * Rien ne bouge à l'écran : la base de Tailwind remet déjà à zéro la marge d'un `<p>`, et
   * l'espacement vient de `space-y-1` sur le parent, qui ne distingue pas les deux balises.
   *
   * Une seule fente, `description`, et c'est l'en-tête qui choisit sa balise : un écran n'a pas à
   * savoir ce que le HTML tolère, et une deuxième fente ferait un deuxième endroit où se tromper.
   */
  const inline = typeof description === 'string' || typeof description === 'number';
  const Description = inline ? 'p' : 'div';

  return (
    <div
      className={cn(
        'flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-start sm:justify-between',
        className,
      )}
      {...props}
    >
      <div className="min-w-0 space-y-1">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight sm:text-[1.75rem]">
          {title}
        </h1>
        {description && (
          <Description className="text-sm text-muted-foreground sm:text-[0.9375rem]">
            {description}
          </Description>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">{actions}</div>
      )}
    </div>
  );
}
